// AGC: Emergency Withdraw Page for StakingManagerV4
// AGC: V4 Flow with 2-of-2 Multi-Sig + Staker Voting:
// AGC: 1. Signer proposes (token, amount, wallet from signers, voting duration)
// AGC: 2. BOTH signers must approve → voting countdown starts
// AGC: 3. Stakers vote (stake-weighted, 51% threshold)
// AGC: 4. If passed, signer can execute after voting ends
import React, { useState, useEffect } from 'react'
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, useChainId, usePublicClient } from 'wagmi'
import { formatEther, formatUnits, parseEther, parseUnits } from 'viem'
import { logger } from '../utils/logger'

// AGC: Hardcoded signer addresses from StakingManagerV4 contract
const FIXED_SIGNER_1 = '0xc3594084C18B79c956Ee1A16FB150DEEC5BD0b52'
const FIXED_SIGNER_2 = '0xB5F79EB3b94D4Be8B5f65070CD4408d22F09A99E'

// AGC: V4 TokenType enum values (matches contract)
enum TokenType {
  ETH = 0,
  OGB = 1,
  USDC = 2,
  FARMPRO = 3,
}

// AGC: Token labels for UI
const TOKEN_LABELS: Record<TokenType, string> = {
  [TokenType.ETH]: 'ETH',
  [TokenType.OGB]: 'OGB',
  [TokenType.USDC]: 'USDC',
  [TokenType.FARMPRO]: 'FarmPro',
}

// AGC: StakingManagerV4 ABI
const STAKING_MANAGER_V4_ABI = [
  {
    name: 'getBalances',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'ethBalance', type: 'uint256' },
      { name: 'ogbBalance', type: 'uint256' },
      { name: 'usdcBalance', type: 'uint256' },
      { name: 'farmProBalance', type: 'uint256' },
    ],
  },
  {
    name: 'getSigners',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'signer1', type: 'address' },
      { name: 'signer2', type: 'address' },
    ],
  },
  {
    name: 'proposeWithdrawal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'tokenType', type: 'uint8' },
      { name: 'amount', type: 'uint256' },
      { name: 'votingDurationMinutes', type: 'uint256' },
    ],
    outputs: [{ name: 'proposalId', type: 'uint256' }],
  },
  {
    name: 'approveProposal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [],
  },
  {
    name: 'voteOnProposal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'proposalId', type: 'uint256' },
      { name: 'voteYes', type: 'bool' },
    ],
    outputs: [],
  },
  {
    name: 'executeWithdrawal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [],
  },
  {
    name: 'cancelProposal',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [],
  },
  {
    name: 'getProposal',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [
      { name: 'recipient', type: 'address' },
      { name: 'proposedAt', type: 'uint256' },
      { name: 'votingDurationMinutes', type: 'uint256' },
      { name: 'votingDeadline', type: 'uint256' },
      { name: 'approvedBySigner1', type: 'bool' },
      { name: 'approvedBySigner2', type: 'bool' },
      { name: 'executed', type: 'bool' },
      { name: 'cancelled', type: 'bool' },
      { name: 'stakerVotesYes', type: 'uint256' },
      { name: 'stakerVotesNo', type: 'uint256' },
      { name: 'tokenType', type: 'uint8' },
      { name: 'amount', type: 'uint256' },
    ],
  },
  {
    name: 'canExecuteProposal',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [
      { name: 'canExecute', type: 'bool' },
      { name: 'reason', type: 'string' },
    ],
  },
  {
    name: 'isVotePassed',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'proposalId', type: 'uint256' }],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'hasVoted',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'proposalId', type: 'uint256' },
      { name: 'voter', type: 'address' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'nextProposalId',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'getStakedBalance',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'user', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

// AGC: ERC20 ABI for reading individual token balances
const ERC20_BALANCE_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

// AGC: Contract addresses from environment
// AGC: Updated to V5 (0xB5F6Dd1334d6a317Ec314820403b7f1a3fF71a65) - migrated from V4
const STAKING_MANAGER_V4_ADDRESS = (import.meta.env.VITE_STAKING_MANAGER_ADDRESS || '0xB5F6Dd1334d6a317Ec314820403b7f1a3fF71a65') as `0x${string}`
const STAKING_REVENUE_SHARE_ADDRESS = (import.meta.env.VITE_STAKING_REVENUE_SHARE_ADDRESS || '0x8Ca66F5ba3Daa478a45814b5b59eD21750Dc1804') as `0x${string}`
const OGB_TOKEN_ADDRESS = (import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const USDC_ADDRESS = (import.meta.env.VITE_USDC_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

interface EmergencyWithdrawStakingManagerProps {
  onBack?: () => void
}

// AGC: Proposal Item Component (for collapsible proposal history) - Shared between both contracts
interface ProposalItemProps {
  proposalId: bigint
  revenueShareAddress: `0x${string}`
  stakingManagerAddress: `0x${string}`
  chainId: number
  executionTxHash: string | null
  isExpanded: boolean
  onToggleExpand: () => void
}

const ProposalItem: React.FC<ProposalItemProps> = ({
  proposalId,
  revenueShareAddress,
  stakingManagerAddress,
  chainId,
  executionTxHash,
  isExpanded,
  onToggleExpand,
}) => {
  // AGC: StakingRevenueShare ABI for getProposal
  const REVENUE_SHARE_GET_PROPOSAL_ABI = [
    {
      name: 'getProposal',
      type: 'function',
      stateMutability: 'view',
      inputs: [{ name: 'proposalId', type: 'uint256' }],
      outputs: [
        { name: 'recipient', type: 'address' },
        { name: 'proposedAt', type: 'uint256' },
        { name: 'votingDeadline', type: 'uint256' },
        { name: 'approved', type: 'bool' },
        { name: 'executed', type: 'bool' },
        { name: 'cancelled', type: 'bool' },
        { name: 'stakerVotesYes', type: 'uint256' },
        { name: 'stakerVotesNo', type: 'uint256' },
        { name: 'tokenType', type: 'uint8' },
        { name: 'amount', type: 'uint256' },
      ],
    },
    {
      name: 'isVotePassed',
      type: 'function',
      stateMutability: 'view',
      inputs: [{ name: 'proposalId', type: 'uint256' }],
      outputs: [{ name: '', type: 'bool' }],
    },
  ] as const

  // AGC: Read proposal data from StakingRevenueShare
  const { data: proposalDataRevenueShare } = useReadContract({
    address: revenueShareAddress,
    abi: REVENUE_SHARE_GET_PROPOSAL_ABI,
    functionName: 'getProposal',
    args: [proposalId],
    query: {
      enabled: true,
      refetchInterval: 5000,
    },
  })

  // AGC: Read proposal data from StakingManager (shared proposal IDs)
  const { data: proposalDataStakingManager } = useReadContract({
    address: stakingManagerAddress,
    abi: STAKING_MANAGER_V4_ABI,
    functionName: 'getProposal',
    args: [proposalId],
    query: {
      enabled: true,
      refetchInterval: 5000,
    },
  })

  // AGC: Determine which contract has the proposal and get the data
  const proposalData = proposalDataRevenueShare || proposalDataStakingManager
  const contractAddress = proposalDataRevenueShare ? revenueShareAddress : stakingManagerAddress
  const contractType = proposalDataRevenueShare ? 'StakingRevenueShare' : 'StakingManager'
  const isStakingManager = !!proposalDataStakingManager

  // AGC: Check if vote passed
  const { data: votePassed } = useReadContract({
    address: contractAddress,
    abi: isStakingManager ? STAKING_MANAGER_V4_ABI : REVENUE_SHARE_GET_PROPOSAL_ABI,
    functionName: 'isVotePassed',
    args: [proposalId],
    query: {
      enabled: !!proposalData,
    },
  })

  if (!proposalData) {
    return null
  }

  // AGC: Parse proposal data based on contract type
  const proposal = isStakingManager
    ? {
        // AGC: StakingManager structure
        recipient: proposalData[0] as string,
        proposedAt: proposalData[1] as bigint,
        votingDurationMinutes: proposalData[2] as bigint,
        votingDeadline: proposalData[3] as bigint,
        approvedBySigner1: proposalData[4] as boolean,
        approvedBySigner2: proposalData[5] as boolean,
        executed: proposalData[6] as boolean,
        cancelled: proposalData[7] as boolean,
        stakerVotesYes: BigInt(proposalData[8]?.toString() || '0'), // AGC: Ensure BigInt conversion
        stakerVotesNo: BigInt(proposalData[9]?.toString() || '0'), // AGC: Ensure BigInt conversion
        tokenType: proposalData[10] as number,
        amount: (() => {
          const amt = proposalData[11]
          if (amt === undefined || amt === null) return 0n
          if (typeof amt === 'bigint') return amt
          try {
            return BigInt(amt.toString())
          } catch {
            return 0n
          }
        })(), // AGC: Safe BigInt conversion with fallback
      }
    : {
        // AGC: StakingRevenueShare structure
        recipient: proposalData[0] as string,
        proposedAt: proposalData[1] as bigint,
        votingDeadline: proposalData[2] as bigint,
        approved: proposalData[3] as boolean,
        executed: proposalData[4] as boolean,
        cancelled: proposalData[5] as boolean,
        stakerVotesYes: BigInt(proposalData[6]?.toString() || '0'), // AGC: Ensure BigInt conversion
        stakerVotesNo: BigInt(proposalData[7]?.toString() || '0'), // AGC: Ensure BigInt conversion
        tokenType: proposalData[8] as number,
        amount: (() => {
          const amt = proposalData[9]
          if (amt === undefined || amt === null) return 0n
          if (typeof amt === 'bigint') return amt
          try {
            return BigInt(amt.toString())
          } catch {
            return 0n
          }
        })(), // AGC: Safe BigInt conversion with fallback
        // AGC: Add StakingManager fields for compatibility
        approvedBySigner1: proposalData[3] as boolean,
        approvedBySigner2: proposalData[3] as boolean,
      }

  // AGC: Calculate vote percentages with proper BigInt handling
  const stakerVotesYes = BigInt(proposal.stakerVotesYes?.toString() || '0')
  const stakerVotesNo = BigInt(proposal.stakerVotesNo?.toString() || '0')
  const totalVotes = stakerVotesYes + stakerVotesNo
  const yesPercent = totalVotes > 0n ? Number((stakerVotesYes * 100n) / totalVotes) : 0
  const bothApproved = isStakingManager ? (proposal.approvedBySigner1 && proposal.approvedBySigner2) : proposal.approved

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      {/* AGC: Proposal Header (Clickable to expand) */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-800/50 hover:bg-slate-700/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">
            {contractType === 'StakingRevenueShare' ? 'SRS' : 'SM'} #{proposalId.toString()}
          </span>
          <span className={`text-xs font-semibold ${
            proposal.executed ? 'text-green-400' : 
            proposal.cancelled ? 'text-red-400' : 
            'text-yellow-400'
          }`}>
            {proposal.executed ? '✅ Executed' : proposal.cancelled ? '❌ Cancelled' : '⏳ Active'}
          </span>
          {proposal.executed && executionTxHash && (
            <a
              href={`${chainId === 84532 ? 'https://sepolia.basescan.org' : 'https://basescan.org'}/tx/${executionTxHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              🔗
            </a>
          )}
        </div>
        <span className="text-xs text-slate-400">{isExpanded ? '▼' : '▶'}</span>
      </button>
      
      {/* AGC: Proposal Details (shown when expanded) */}
      {isExpanded && (
        <div className="px-3 py-2 bg-slate-900/50 border-t border-slate-700">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-slate-500">Token</p>
              <p className="font-bold text-white">{TOKEN_LABELS[proposal.tokenType as TokenType] || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-slate-500">Amount</p>
              <p className="font-bold text-white">
                {!proposal.amount || proposal.amount === undefined || proposal.amount === null 
                  ? 'N/A' 
                  : proposal.amount === 0n 
                    ? 'All' 
                    : formatEther(proposal.amount || 0n)}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Recipient</p>
              <p className="font-mono text-xs text-slate-300 break-all">
                {proposal.recipient.slice(0, 6)}...{proposal.recipient.slice(-4)}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Vote Status</p>
              <p className={votePassed ? 'text-green-400' : 'text-yellow-400'}>
                {yesPercent}% YES {votePassed ? '✅' : ''}
              </p>
            </div>
            {isStakingManager ? (
              <>
                <div>
                  <p className="text-slate-500">Signer 1</p>
                  <p className={proposal.approvedBySigner1 ? 'text-green-400' : 'text-slate-500'}>
                    {proposal.approvedBySigner1 ? '✅ Yes' : '⏳ Pending'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Signer 2</p>
                  <p className={proposal.approvedBySigner2 ? 'text-green-400' : 'text-slate-500'}>
                    {proposal.approvedBySigner2 ? '✅ Yes' : '⏳ Pending'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Both Approved</p>
                  <p className={bothApproved ? 'text-green-400' : 'text-slate-500'}>
                    {bothApproved ? '✅ Yes' : '⏳ Pending'}
                  </p>
                </div>
              </>
            ) : (
              <div>
                <p className="text-slate-500">Approved</p>
                <p className={proposal.approved ? 'text-green-400' : 'text-slate-500'}>
                  {proposal.approved ? '✅ Yes' : '⏳ Pending'}
                </p>
              </div>
            )}
            <div>
              <p className="text-slate-500">Proposed</p>
              <p className="text-xs text-slate-400">
                {new Date(Number(proposal.proposedAt) * 1000).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const EmergencyWithdrawStakingManager: React.FC<EmergencyWithdrawStakingManagerProps> = ({ onBack }) => {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { writeContractAsync, isPending } = useWriteContract()
  const publicClient = usePublicClient()
  const [proposalId, setProposalId] = useState<bigint | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [executionTxHash, setExecutionTxHash] = useState<string | null>(null)
  const [shouldAutoLoadProposal, setShouldAutoLoadProposal] = useState(true) // AGC: Flag to prevent auto-loading when user wants to start new proposal
  
  // AGC: Store tx hashes for all proposals (persisted in localStorage - shared with StakingRevenueShare)
  const [proposalTxHashes, setProposalTxHashes] = useState<Record<string, string>>(() => {
    try {
      const stored = localStorage.getItem('emergencyWithdrawProposalTxHashes')
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })
  
  // AGC: Proposal history state
  const [isProposalHistoryExpanded, setIsProposalHistoryExpanded] = useState(false)
  const [expandedProposals, setExpandedProposals] = useState<Set<bigint>>(new Set())
  
  // AGC: V4 Proposal form state
  const [tokenType, setTokenType] = useState<TokenType>(TokenType.ETH)
  const [withdrawAmount, setWithdrawAmount] = useState<string>('0')
  const [selectedRecipient, setSelectedRecipient] = useState<string>(FIXED_SIGNER_1)
  const [votingDurationMinutes, setVotingDurationMinutes] = useState<string>('5')

  // AGC: ETH balance state (read via JSON-RPC)
  const [ethBalanceValue, setEthBalanceValue] = useState<bigint>(0n)

  // AGC: Check if current user is a signer
  const isSigner1 = address?.toLowerCase() === FIXED_SIGNER_1.toLowerCase()
  const isSigner2 = address?.toLowerCase() === FIXED_SIGNER_2.toLowerCase()
  const isSigner = isSigner1 || isSigner2

  // AGC: Read contract balances
  const { data: ogbBalanceRaw, refetch: refetchOgb } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: ERC20_BALANCE_ABI,
    functionName: 'balanceOf',
    args: [STAKING_MANAGER_V4_ADDRESS],
    query: {
      enabled: isConnected && STAKING_MANAGER_V4_ADDRESS !== '0x0000000000000000000000000000000000000000',
      refetchInterval: 5000,
    },
  })

  const { data: usdcBalanceRaw, refetch: refetchUsdc } = useReadContract({
    address: USDC_ADDRESS,
    abi: ERC20_BALANCE_ABI,
    functionName: 'balanceOf',
    args: [STAKING_MANAGER_V4_ADDRESS],
    query: {
      enabled: isConnected && STAKING_MANAGER_V4_ADDRESS !== '0x0000000000000000000000000000000000000000',
      refetchInterval: 5000,
    },
  })

  // AGC: Fetch ETH balance via JSON-RPC
  useEffect(() => {
    const fetchEthBalance = async () => {
      if (STAKING_MANAGER_V4_ADDRESS === '0x0000000000000000000000000000000000000000') return
      try {
        const rpcUrl = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
        const response = await fetch(rpcUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_getBalance',
            params: [STAKING_MANAGER_V4_ADDRESS, 'latest'],
            id: 1,
          }),
        })
        const data = await response.json()
        if (data.result) setEthBalanceValue(BigInt(data.result))
      } catch (e) {
        logger.warn('Failed to fetch ETH balance:', e)
      }
    }
    fetchEthBalance()
    const interval = setInterval(fetchEthBalance, 5000)
    return () => clearInterval(interval)
  }, [STAKING_MANAGER_V4_ADDRESS])

  // AGC: Refetch all balances
  const refetchBalances = async () => {
    await Promise.all([
      refetchOgb(),
      refetchUsdc(),
    ])
    // AGC: Re-fetch ETH balance
    try {
      const rpcUrl = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
      const response = await fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'eth_getBalance',
          params: [STAKING_MANAGER_V4_ADDRESS, 'latest'],
          id: 1,
        }),
      })
      const data = await response.json()
      if (data.result) {
        setEthBalanceValue(BigInt(data.result))
      }
    } catch (err) {
      logger.warn('Failed to refetch ETH balance:', err)
    }
  }

  // AGC: Read next proposal ID from StakingManager
  const { data: nextProposalIdStakingManager, refetch: refetchNextProposalIdStakingManager } = useReadContract({
    address: STAKING_MANAGER_V4_ADDRESS,
    abi: STAKING_MANAGER_V4_ABI,
    functionName: 'nextProposalId',
    query: {
      refetchInterval: 3000,
    },
  })

  // AGC: StakingRevenueShare ABI for nextProposalId (same structure)
  const REVENUE_SHARE_NEXT_PROPOSAL_ID_ABI = [
    {
      name: 'nextProposalId',
      type: 'function',
      stateMutability: 'view',
      inputs: [],
      outputs: [{ name: '', type: 'uint256' }],
    },
  ] as const

  // AGC: Read next proposal ID from StakingRevenueShare (shared proposal history)
  const { data: nextProposalIdRevenueShare, refetch: refetchNextProposalIdRevenueShare } = useReadContract({
    address: STAKING_REVENUE_SHARE_ADDRESS,
    abi: REVENUE_SHARE_NEXT_PROPOSAL_ID_ABI,
    functionName: 'nextProposalId',
    query: {
      refetchInterval: 3000,
    },
  })

  // AGC: Refetch both nextProposalIds
  const refetchNextProposalId = async () => {
    await Promise.all([
      refetchNextProposalIdStakingManager(),
      refetchNextProposalIdRevenueShare(),
    ])
  }

  // AGC: Generate separate proposal ID arrays for each contract (each maintains its own counter)
  const stakingManagerProposalIds = nextProposalIdStakingManager !== undefined && nextProposalIdStakingManager > 0n
    ? Array.from({ length: Number(nextProposalIdStakingManager) }, (_, i) => ({ id: BigInt(i), contract: 'StakingManager' as const })).reverse()
    : []
  
  const revenueShareProposalIds = nextProposalIdRevenueShare !== undefined && nextProposalIdRevenueShare > 0n
    ? Array.from({ length: Number(nextProposalIdRevenueShare) }, (_, i) => ({ id: BigInt(i), contract: 'StakingRevenueShare' as const })).reverse()
    : []

  // AGC: Combine and sort by proposal ID (newest first, but grouped by contract)
  const allProposalIds = [...stakingManagerProposalIds, ...revenueShareProposalIds]
    .sort((a, b) => {
      // AGC: Sort by contract first (StakingManager first), then by ID (newest first)
      if (a.contract !== b.contract) {
        return a.contract === 'StakingManager' ? -1 : 1
      }
      return Number(b.id) - Number(a.id)
    })

  // AGC: Read current proposal
  const { data: proposal, refetch: refetchProposal } = useReadContract({
    address: STAKING_MANAGER_V4_ADDRESS,
    abi: STAKING_MANAGER_V4_ABI,
    functionName: 'getProposal',
    args: proposalId !== null ? [proposalId] : undefined,
    query: {
      enabled: proposalId !== null,
      refetchInterval: 5000,
    },
  })

  // AGC: Check if proposal can be executed
  const { data: canExecuteData, refetch: refetchCanExecute } = useReadContract({
    address: STAKING_MANAGER_V4_ADDRESS,
    abi: STAKING_MANAGER_V4_ABI,
    functionName: 'canExecuteProposal',
    args: proposalId !== null ? [proposalId] : undefined,
    query: {
      enabled: proposalId !== null,
      refetchInterval: 5000,
    },
  })

  // AGC: Check if vote passed
  const { data: votePassed, refetch: refetchVotePassed } = useReadContract({
    address: STAKING_MANAGER_V4_ADDRESS,
    abi: STAKING_MANAGER_V4_ABI,
    functionName: 'isVotePassed',
    args: proposalId !== null ? [proposalId] : undefined,
    query: {
      enabled: proposalId !== null,
      refetchInterval: 5000,
    },
  })

  // AGC: Read user's staked balance
  const { data: userStakeBalanceRaw } = useReadContract({
    address: STAKING_MANAGER_V4_ADDRESS,
    abi: STAKING_MANAGER_V4_ABI,
    functionName: 'getStakedBalance',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
    },
  })

  // AGC: Check if user has voted
  const { data: hasVoted } = useReadContract({
    address: STAKING_MANAGER_V4_ADDRESS,
    abi: STAKING_MANAGER_V4_ABI,
    functionName: 'hasVoted',
    args: proposalId !== null && address ? [proposalId, address] : undefined,
    query: {
      enabled: proposalId !== null && !!address,
    },
  })

  // AGC: Use stake from StakingManager
  const userStakeBalance = userStakeBalanceRaw ? (userStakeBalanceRaw as bigint) : 0n
  const isStaker = userStakeBalance > 0n

  // AGC: Load latest proposal ID on mount (only if we haven't set one manually)
  useEffect(() => {
    // AGC: If StakingManager has proposals, load the latest one
    // AGC: Only auto-load if proposalId is null AND shouldAutoLoadProposal is true
    if (nextProposalIdStakingManager !== undefined && nextProposalIdStakingManager > 0n && proposalId === null && shouldAutoLoadProposal) {
      setProposalId(nextProposalIdStakingManager - 1n)
    }
  }, [nextProposalIdStakingManager, proposalId, shouldAutoLoadProposal])

  // AGC: Parse proposal data
  const proposalData = proposal ? {
    recipient: proposal[0] as string,
    proposedAt: proposal[1] as bigint,
    votingDurationMinutes: proposal[2] as bigint,
    votingDeadline: proposal[3] as bigint,
    approvedBySigner1: proposal[4] as boolean,
    approvedBySigner2: proposal[5] as boolean,
    executed: proposal[6] as boolean,
    cancelled: proposal[7] as boolean,
    stakerVotesYes: proposal[8] as bigint,
    stakerVotesNo: proposal[9] as bigint,
    tokenType: proposal[10] as number,
    amount: proposal[11] as bigint,
  } : null

  // AGC: Parse canExecute data
  const canExecute = canExecuteData ? canExecuteData[0] as boolean : false
  const executeReason = canExecuteData ? canExecuteData[1] as string : ''

  // AGC: allProposalIds is now generated above with contract information

  // AGC: Format balances from individual reads
  const ethBalance = formatEther(ethBalanceValue || 0n)
  const ogbBalance = formatEther(ogbBalanceRaw ? BigInt(ogbBalanceRaw.toString()) : 0n)
  const usdcBalance = formatUnits(usdcBalanceRaw ? BigInt(usdcBalanceRaw.toString()) : 0n, 6)
  
  // AGC: Show FarmPro only on Mainnet (4th column)
  const isMainnet = chainId === 8453
  const farmProBalance = '0' // AGC: FarmPro balance (only available on mainnet)

  // AGC: Calculate vote percentages
  const totalVotes = proposalData ? proposalData.stakerVotesYes + proposalData.stakerVotesNo : 0n
  const yesPercentage = totalVotes > 0n && proposalData
    ? Number((proposalData.stakerVotesYes * 100n) / totalVotes)
    : 0

  // AGC: Determine current step
  const getCurrentStep = () => {
    // AGC: Check proposalId !== null (not !proposalId) because 0n is falsy but valid
    if (proposalId === null || !proposalData) return 1
    if (proposalData.executed) return 5
    if (proposalData.cancelled) return 0
    const bothApproved = proposalData.approvedBySigner1 && proposalData.approvedBySigner2
    if (!bothApproved) return 2 // AGC: Need BOTH signers to approve (2-of-2)
    if (proposalData.votingDeadline > 0n && BigInt(Date.now()) / 1000n <= proposalData.votingDeadline) return 3
    if (canExecute) return 4
    return 3 // Still in voting or vote failed
  }

  const currentStep = getCurrentStep()
  const steps = [
    { num: 1, label: 'Propose', icon: '📝' },
    { num: 2, label: 'Both Approve', icon: '✅' },
    { num: 3, label: 'Vote', icon: '🗳️' },
    { num: 4, label: 'Execute', icon: '🚀' },
    { num: 5, label: 'Done', icon: '🎉' },
  ]

  // AGC: Calculate remaining time
  const getRemainingTime = () => {
    if (!proposalData || proposalData.votingDeadline === 0n) return null
    const remaining = Number(proposalData.votingDeadline) * 1000 - Date.now()
    if (remaining <= 0) return 'Ended'
    const minutes = Math.floor(remaining / 60000)
    const seconds = Math.floor((remaining % 60000) / 1000)
    return `${minutes}m ${seconds}s`
  }

  // AGC: V4 Step 1: Propose withdrawal
  const handleProposeWithdrawal = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isSigner) {
      setError('Only signers can propose withdrawals')
      return
    }

    const durationMinutes = parseInt(votingDurationMinutes) || 5
    if (durationMinutes < 1 || durationMinutes > 10080) {
      setError('Voting duration must be between 1 and 10080 minutes (7 days)')
      return
    }

    // AGC: Each contract maintains its own counter - no need to unify
    const stakingManagerNextId = nextProposalIdStakingManager !== undefined ? nextProposalIdStakingManager : 0n

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const amount = withdrawAmount === '0' || withdrawAmount === '' 
        ? 0n 
        : tokenType === TokenType.USDC
          ? parseUnits(withdrawAmount, 6)
          : parseEther(withdrawAmount)

      logger.info('V4 proposeWithdrawal (StakingManager):', {
        recipient: selectedRecipient,
        tokenType,
        amount: amount.toString(),
        votingDurationMinutes: durationMinutes,
        expectedProposalId: stakingManagerNextId.toString(), // AGC: This is what the contract will assign (SM #X)
      })

      const hash = await writeContractAsync({
        address: STAKING_MANAGER_V4_ADDRESS,
        abi: STAKING_MANAGER_V4_ABI,
        functionName: 'proposeWithdrawal',
        args: [
          selectedRecipient as `0x${string}`,
          tokenType,
          amount,
          BigInt(durationMinutes),
        ],
      })

      logger.info('Proposal submitted:', hash)
      
      // AGC: The contract will assign the proposal ID based on its own counter
      // AGC: Display with "SM" prefix for StakingManager
      const contractAssignedId = stakingManagerNextId
      
      setSuccess(`SM #${contractAssignedId} submitted! TX: ${hash}`)

      // AGC: Wait for transaction to be mined
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      // AGC: Set the proposal ID that the contract actually assigned
      setProposalId(contractAssignedId)
      setShouldAutoLoadProposal(true) // AGC: Re-enable auto-loading after creating new proposal
      setError(null) // AGC: Clear any previous errors
      
      // AGC: Refetch all data to update unified counter
      await refetchNextProposalId()
      await refetchProposal()
    } catch (err: any) {
      logger.error('Failed to propose withdrawal:', err)
      setError(err.message || 'Failed to propose withdrawal')
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: V4 Step 2: Approve proposal (2-of-2 required)
  const handleApproveProposal = async () => {
    if (!isConnected || !address || !isSigner) {
      setError('You must be a signer to approve')
      return
    }

    if (proposalId === null) {
      setError('No proposal to approve')
      return
    }

    // AGC: Pre-flight checks to avoid "Unable to estimate network fee" errors
    if (!proposalData) {
      setError('Proposal data not loaded. Please wait...')
      return
    }

    // AGC: Check if already approved by this signer
    if (isSigner1 && proposalData.approvedBySigner1) {
      setError('You (Signer 1) have already approved this proposal. Waiting for Signer 2 to approve.')
      return
    }

    if (isSigner2 && proposalData.approvedBySigner2) {
      setError('You (Signer 2) have already approved this proposal. Waiting for Signer 1 to approve.')
      return
    }

    // AGC: Check if voting has already started (both approved)
    if (proposalData.votingDeadline > 0n) {
      setError('Voting has already started. Both signers have approved.')
      return
    }

    // AGC: Check if proposal is cancelled or executed
    if (proposalData.cancelled) {
      setError('This proposal has been cancelled.')
      return
    }

    if (proposalData.executed) {
      setError('This proposal has already been executed.')
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    const contractConfig = {
      address: STAKING_MANAGER_V4_ADDRESS,
      abi: STAKING_MANAGER_V4_ABI,
      functionName: 'approveProposal' as const,
      args: [proposalId],
    }

    try {
      logger.info('Attempting to approve proposal:', {
        proposalId: proposalId.toString(),
        address: address,
        isSigner1: isSigner1,
        isSigner2: isSigner2,
        proposalState: {
          approvedBySigner1: proposalData.approvedBySigner1,
          approvedBySigner2: proposalData.approvedBySigner2,
          votingDeadline: proposalData.votingDeadline.toString(),
          cancelled: proposalData.cancelled,
          executed: proposalData.executed,
        }
      })

      // AGC: First attempt: Let wallet handle it
      const hash = await writeContractAsync(contractConfig)
      logger.info('Approval submitted:', hash)
      setSuccess(`Approved! TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      refetchProposal()
      refetchCanExecute()
    } catch (err: any) {
      // AGC: If direct call fails with estimation error, try manual gas estimation fallback
      if (err.message?.includes('Unable to estimate') || err.message?.includes('estimate')) {
        console.warn('⚠️ AGC: Direct approve failed, attempting manual gas fallback...', err)
        
        if (!publicClient || !address) {
          logger.error('Failed to approve (no publicClient or address):', err)
          setError(err.message || 'Failed to approve proposal')
          setIsLoading(false)
          return
        }

        try {
          // AGC: Estimate gas manually to avoid wallet-side estimation failures
          const gasEstimate = await publicClient.estimateContractGas({
            ...contractConfig,
            account: address
          })

          let feeOverrides: {
            gas?: bigint
            maxFeePerGas?: bigint
            maxPriorityFeePerGas?: bigint
            gasPrice?: bigint
          } = { gas: gasEstimate }

          try {
            // AGC: Prefer EIP-1559 fee data when available
            const feeData = await publicClient.estimateFeesPerGas()
            feeOverrides = {
              gas: gasEstimate,
              maxFeePerGas: feeData.maxFeePerGas,
              maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
            }
          } catch (feeError) {
            console.warn('⚠️ AGC: Failed to estimate EIP-1559 fees, falling back to gasPrice', feeError)
            try {
              const gasPrice = await publicClient.getGasPrice()
              feeOverrides = {
                gas: gasEstimate,
                gasPrice
              }
            } catch (gasPriceError) {
              console.warn('⚠️ AGC: Unable to fetch gasPrice fallback:', gasPriceError)
            }
          }

          // AGC: Retry with manual gas
          const hash = await writeContractAsync({
            ...contractConfig,
            ...feeOverrides
          })

          logger.info('Approval submitted (manual gas):', hash)
          setSuccess(`Approved! TX: ${hash}`)

          await new Promise(resolve => setTimeout(resolve, 3000))
          refetchProposal()
          refetchCanExecute()
          setIsLoading(false)
          return
        } catch (fallbackErr: any) {
          // AGC: Manual gas estimation also failed - show original error
          logger.error('Failed to approve (manual gas fallback also failed):', fallbackErr)
          err = fallbackErr
        }
      }

      logger.error('Failed to approve:', err)
      
      // AGC: Provide more detailed error messages
      let errorMessage = 'Failed to approve proposal'
      
      if (err.message?.includes('Signer1 already approved') || err.message?.includes('Signer2 already approved')) {
        errorMessage = `You have already approved this proposal. ${isSigner1 ? 'Waiting for Signer 2' : 'Waiting for Signer 1'} to approve.`
      } else if (err.message?.includes('Already fully approved') || err.message?.includes('votingDeadline')) {
        errorMessage = 'Both signers have already approved. Voting has started.'
      } else if (err.message?.includes('Proposal cancelled')) {
        errorMessage = 'This proposal has been cancelled.'
      } else if (err.message?.includes('Already executed')) {
        errorMessage = 'This proposal has already been executed.'
      } else if (err.message?.includes('does not exist')) {
        errorMessage = 'Proposal does not exist on-chain.'
      } else if (err.message?.includes('Unable to estimate')) {
        errorMessage = 'Unable to estimate gas. This usually means the transaction would fail.\n' +
          `Possible reasons:\n` +
          `1. You already approved (Signer 1: ${proposalData.approvedBySigner1}, Signer 2: ${proposalData.approvedBySigner2})\n` +
          `2. Voting already started (deadline: ${proposalData.votingDeadline > 0n ? 'Yes' : 'No'})\n` +
          `3. Proposal is cancelled or executed\n` +
          `\nYour address: ${address}\n` +
          `You are: ${isSigner1 ? 'Signer 1' : isSigner2 ? 'Signer 2' : 'Not a signer'}`
      } else {
        errorMessage = err.message || 'Failed to approve proposal'
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: V4 Step 3: Vote
  const handleVote = async (voteYes: boolean) => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isStaker) {
      setError('You must be a staker to vote')
      return
    }

    if (proposalId === null) {
      setError('No proposal to vote on')
      return
    }

    if (hasVoted) {
      setError('You have already voted')
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const hash = await writeContractAsync({
        address: STAKING_MANAGER_V4_ADDRESS,
        abi: STAKING_MANAGER_V4_ABI,
        functionName: 'voteOnProposal',
        args: [proposalId, voteYes],
      })

      logger.info('Vote submitted:', hash)
      setSuccess(`Vote submitted! TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      refetchProposal()
      refetchVotePassed()
      refetchCanExecute()
    } catch (err: any) {
      logger.error('Failed to vote:', err)
      setError(err.message || 'Failed to vote')
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: Cancel proposal (signer only, can cancel at any step before execution)
  const handleCancelProposal = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isSigner) {
      setError('Only signers can cancel proposals. You must be Signer 1 or Signer 2.')
      return
    }

    if (proposalId === null) {
      setError('No proposal to cancel')
      return
    }

    if (proposalData?.executed) {
      setError('Cannot cancel an executed proposal')
      return
    }

    if (proposalData?.cancelled) {
      setError('Proposal is already cancelled')
      return
    }

    // AGC: Confirm cancellation
    if (!confirm('Are you sure you want to cancel this proposal? This action cannot be undone.')) {
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      // AGC: First, try to read the proposal to verify it exists and check state
      const proposalCheck = await refetchProposal()
      if (!proposalCheck.data) {
        setError('Proposal not found on-chain. It may have been deleted or never created.')
        setIsLoading(false)
        return
      }

      logger.info('Attempting to cancel proposal:', {
        proposalId: proposalId.toString(),
        address: address,
        isSigner1: isSigner1,
        isSigner2: isSigner2,
        proposalState: {
          executed: proposalData?.executed,
          cancelled: proposalData?.cancelled,
          recipient: proposalData?.recipient,
        }
      })

      const contractConfig = {
        address: STAKING_MANAGER_V4_ADDRESS,
        abi: STAKING_MANAGER_V4_ABI,
        functionName: 'cancelProposal' as const,
        args: [proposalId],
      }

      // AGC: First attempt: Let wallet handle it
      let hash: `0x${string}`
      try {
        hash = await writeContractAsync(contractConfig)
      } catch (err: any) {
        // AGC: If direct call fails with estimation error, try manual gas estimation fallback
        if (err.message?.includes('Unable to estimate') || err.message?.includes('estimate')) {
          console.warn('⚠️ AGC: Direct cancel failed, attempting manual gas fallback...', err)
          
          if (!publicClient || !address) {
            throw err
          }

          // AGC: Estimate gas manually to avoid wallet-side estimation failures
          const gasEstimate = await publicClient.estimateContractGas({
            ...contractConfig,
            account: address
          })

          let feeOverrides: {
            gas?: bigint
            maxFeePerGas?: bigint
            maxPriorityFeePerGas?: bigint
            gasPrice?: bigint
          } = { gas: gasEstimate }

          try {
            // AGC: Prefer EIP-1559 fee data when available
            const feeData = await publicClient.estimateFeesPerGas()
            feeOverrides = {
              gas: gasEstimate,
              maxFeePerGas: feeData.maxFeePerGas,
              maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
            }
          } catch (feeError) {
            console.warn('⚠️ AGC: Failed to estimate EIP-1559 fees, falling back to gasPrice', feeError)
            try {
              const gasPrice = await publicClient.getGasPrice()
              feeOverrides = {
                gas: gasEstimate,
                gasPrice
              }
            } catch (gasPriceError) {
              console.warn('⚠️ AGC: Unable to fetch gasPrice fallback:', gasPriceError)
            }
          }

          // AGC: Retry with manual gas
          hash = await writeContractAsync({
            ...contractConfig,
            ...feeOverrides
          })
        } else {
          throw err
        }
      }

      logger.info('Proposal cancelled:', hash)
      setSuccess(`Proposal cancelled! TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      
      // AGC: Reset state to allow new proposal
      setProposalId(null)
      setExecutionTxHash(null)
      setWithdrawAmount('0')
      setVotingDurationMinutes('5')
      setTokenType(TokenType.ETH)
      setSelectedRecipient(FIXED_SIGNER_1)
      setShouldAutoLoadProposal(false) // AGC: Prevent auto-loading after cancel
      
      // AGC: Refetch data
      await refetchNextProposalId()
      await refetchProposal()
    } catch (err: any) {
      logger.error('Failed to cancel proposal:', err)
      
      // AGC: Provide more detailed error messages
      let errorMessage = 'Failed to cancel proposal'
      
      if (err.message?.includes('Not owner or signer') || err.message?.includes('Not authorized')) {
        errorMessage = 'You are not authorized to cancel. Only Signer 1, Signer 2, or the contract owner can cancel proposals.'
      } else if (err.message?.includes('Already cancelled')) {
        errorMessage = 'This proposal is already cancelled.'
      } else if (err.message?.includes('Already executed')) {
        errorMessage = 'Cannot cancel an executed proposal.'
      } else if (err.message?.includes('does not exist')) {
        errorMessage = 'Proposal does not exist on-chain.'
      } else if (err.message?.includes('Unable to estimate')) {
        errorMessage = 'Unable to estimate gas. This usually means the transaction would fail. Possible reasons:\n' +
          '1. You are not a signer or owner\n' +
          '2. Proposal is already cancelled/executed\n' +
          '3. Contract function may not exist at this address\n' +
          `\nYour address: ${address}\n` +
          `Signer 1: ${FIXED_SIGNER_1}\n` +
          `Signer 2: ${FIXED_SIGNER_2}\n` +
          `\nIf you are a signer, try having the OTHER signer approve the proposal instead to move it forward.`
      } else {
        errorMessage = err.message || 'Failed to cancel proposal'
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: V4 Step 4: Execute (after vote passes)
  const handleExecuteWithdrawal = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!isSigner) {
      setError('Only signers can execute')
      return
    }

    if (proposalId === null) {
      setError('No proposal to execute')
      return
    }

    if (!canExecute) {
      setError(`Cannot execute: ${executeReason}`)
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const hash = await writeContractAsync({
        address: STAKING_MANAGER_V4_ADDRESS,
        abi: STAKING_MANAGER_V4_ABI,
        functionName: 'executeWithdrawal',
        args: [proposalId],
      })

      logger.info('Withdrawal executed:', hash)
      setExecutionTxHash(hash)
      
      // AGC: Store tx hash in localStorage for proposal history (shared with StakingRevenueShare)
      // AGC: Use "SM-{id}" format to avoid conflicts with SRS proposals
      const proposalKey = `SM-${proposalId.toString()}`
      const updatedHashes = { ...proposalTxHashes, [proposalKey]: hash, [proposalId.toString()]: hash } // AGC: Store both formats for backward compatibility
      setProposalTxHashes(updatedHashes)
      localStorage.setItem('emergencyWithdrawProposalTxHashes', JSON.stringify(updatedHashes))
      
      setSuccess(`Withdrawal executed! TX: ${hash}`)

      await new Promise(resolve => setTimeout(resolve, 3000))
      refetchBalances()
      refetchProposal()
    } catch (err: any) {
      logger.error('Failed to execute:', err)
      setError(err.message || 'Failed to execute withdrawal')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white" style={{ paddingTop: 'env(safe-area-inset-top, 0)', paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
      <div className="max-w-md mx-auto px-4 pb-20">
        {/* AGC: Header */}
        <div className="flex items-center justify-between pt-4 pb-3 border-b border-white/10">
          <h1 className="text-xl font-bold text-red-400">🚨 Emergency Withdraw V4</h1>
          {onBack && (
            <button
              onClick={onBack}
              className="px-3 py-1.5 bg-slate-700/50 hover:bg-slate-600 rounded-lg text-sm transition-colors"
            >
              ← Back
            </button>
          )}
        </div>

        {/* AGC: Error/Success Messages */}
        {(error || success) && (
          <div className={`mt-3 p-2.5 rounded-lg text-xs ${error ? 'bg-red-900/30 border border-red-500/50' : 'bg-green-900/30 border border-green-500/50'}`}>
            <p className={error ? 'text-red-200' : 'text-green-200'}>
              {error ? '❌' : '✅'} {error || success}
            </p>
          </div>
        )}

        {/* AGC: Step Progress */}
        <div className="mt-4 mb-4">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Step {currentStep} of 5</span>
            <span>{steps.find(s => s.num === currentStep)?.label || 'Complete'}</span>
          </div>
          <div className="flex gap-1">
            {steps.map((step) => (
              <div
                key={step.num}
                className={`flex-1 h-1.5 rounded-full transition-all ${
                  step.num < currentStep
                    ? 'bg-green-500'
                    : step.num === currentStep
                    ? currentStep === 5 ? 'bg-green-500' : 'bg-yellow-500'  // AGC: Step 5 = done = green
                    : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>

        {/* AGC: Balances Grid */}
        <div className="mt-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm uppercase tracking-wider text-slate-400">💰 Contract Funds</h2>
              <span className="text-xs text-slate-500">(StakingManager)</span>
              <a
                href={`${chainId === 84532 ? 'https://sepolia.basescan.org' : 'https://basescan.org'}/address/${STAKING_MANAGER_V4_ADDRESS}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 text-xs"
                title={`View StakingManager contract on BaseScan`}
              >
                🔗
              </a>
            </div>
            <button onClick={() => refetchBalances()} className="text-xs text-slate-400 hover:text-white">🔄</button>
          </div>
          <div className={`grid ${isMainnet ? 'grid-cols-4' : 'grid-cols-3'} gap-2`}>
            <div className="text-center">
              <p className="text-xs text-slate-500">ETH</p>
              <p className="text-lg font-bold">{parseFloat(ethBalance).toFixed(4)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">OGB</p>
              <p className="text-lg font-bold">{parseFloat(ogbBalance).toFixed(2)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">USDC</p>
              <p className="text-lg font-bold">{parseFloat(usdcBalance).toFixed(2)}</p>
            </div>
            {isMainnet && (
              <div className="text-center">
                <p className="text-xs text-slate-500">FarmPro</p>
                <p className="text-lg font-bold">{parseFloat(farmProBalance).toFixed(2)}</p>
              </div>
            )}
          </div>
        </div>

        {/* AGC: User Status */}
        {address && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-2">👤 Your Status</h2>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-slate-500">Role</p>
                <p className="text-sm font-bold">
                  {isSigner ? '🔐 Signer' : isStaker ? '💰 Staker' : '👤 User'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Stake</p>
                <p className={`text-sm font-bold ${isStaker ? 'text-green-400' : 'text-slate-500'}`}>
                  {isStaker ? `${formatEther(userStakeBalance || 0n)} OGB` : '0 OGB'}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">{address.slice(0, 6)}...{address.slice(-4)}</p>
          </div>
        )}

        {/* AGC: Warning for Stuck Proposal */}
        {proposalId !== null && proposalData && currentStep === 2 && !proposalData.executed && !proposalData.cancelled && (
          <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-400 text-sm font-bold mb-2">⚠️ Proposal Stuck at Step 2</p>
            <p className="text-xs text-slate-400 mb-2">
              This proposal is waiting for both signers to approve. If you cannot cancel it, you can:
            </p>
            <ul className="text-xs text-slate-400 list-disc list-inside mb-2 space-y-1">
              <li>Have the other signer approve it (moves to voting)</li>
              <li>Create a new proposal below (this one won't execute without both approvals)</li>
            </ul>
            <button
              onClick={() => {
                // AGC: Reset to allow new proposal
                setProposalId(null)
                setShouldAutoLoadProposal(false)
                setError(null)
                setSuccess(null)
              }}
              className="w-full mt-2 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-xs transition-colors"
            >
              📝 Create New Proposal (Ignore Stuck One)
            </button>
          </div>
        )}

        {/* AGC: Step 1 - Propose Form (Signer Only) - Show if no proposal OR if stuck at step 2 */}
        {((currentStep === 1) || (proposalId !== null && proposalData && currentStep === 2 && !proposalData.executed && !proposalData.cancelled)) && isSigner && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">
              {proposalId !== null && currentStep === 2 ? '📝 Create New Proposal (Current One Stuck)' : '📝 Step 1: Propose Withdrawal'}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Token</label>
                <select
                  value={tokenType}
                  onChange={(e) => setTokenType(Number(e.target.value) as TokenType)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                >
                  <option value={TokenType.ETH}>ETH</option>
                  <option value={TokenType.OGB}>OGB</option>
                  <option value={TokenType.USDC}>USDC</option>
                  <option value={TokenType.FARMPRO}>FarmPro</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Amount (0 = all)</label>
                <input
                  type="text"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Recipient (Signer Only)</label>
                <select
                  value={selectedRecipient}
                  onChange={(e) => setSelectedRecipient(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                >
                  <option value={FIXED_SIGNER_1}>Signer 1 ({FIXED_SIGNER_1.slice(0, 6)}...)</option>
                  <option value={FIXED_SIGNER_2}>Signer 2 ({FIXED_SIGNER_2.slice(0, 6)}...)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Voting Duration (minutes)</label>
                <input
                  type="number"
                  value={votingDurationMinutes}
                  onChange={(e) => setVotingDurationMinutes(e.target.value)}
                  placeholder="5"
                  min="1"
                  max="10080"
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white"
                />
              </div>
              <button
                onClick={handleProposeWithdrawal}
                disabled={isLoading || isPending || !isConnected}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳ Proposing...' : '📝 Propose Withdrawal'}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Cancel Proposal Button (Signer Only, visible when there's an active proposal) */}
        {isSigner && proposalId !== null && proposalData && !proposalData.executed && !proposalData.cancelled && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <button
              onClick={handleCancelProposal}
              disabled={isLoading || isPending || !isConnected}
              className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
            >
              {isLoading ? '⏳ Cancelling...' : '❌ Cancel Proposal'}
            </button>
            <p className="text-xs text-slate-500 text-center mt-2">
              Cancel this proposal to create a new one
            </p>
          </div>
        )}

        {/* AGC: Step 2 - Approve (Both Signers Must Approve) */}
        {currentStep === 2 && isSigner && proposalData && !(proposalData.approvedBySigner1 && proposalData.approvedBySigner2) && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">✅ Step 2: Both Signers Must Approve</h2>
            <div className="mb-3 p-2 bg-slate-800/50 rounded-lg text-xs">
              <p><span className="text-slate-500">Token:</span> {TOKEN_LABELS[proposalData.tokenType as TokenType]}</p>
              <p><span className="text-slate-500">Amount:</span> {proposalData.amount === 0n ? 'All' : formatEther(proposalData.amount)}</p>
              <p><span className="text-slate-500">To:</span> {proposalData.recipient.slice(0, 10)}...</p>
              <p><span className="text-slate-500">Proposal ID:</span> #{proposalId?.toString()}</p>
            </div>
            
            {/* AGC: Diagnostic Info */}
            <div className="mb-3 p-2 bg-blue-900/20 border border-blue-500/30 rounded-lg text-xs">
              <p className="text-blue-400 font-bold mb-1">🔍 Diagnostic Info:</p>
              <p className="text-slate-300">Your Address: {address?.slice(0, 10)}...</p>
              <p className="text-slate-300">You are: {isSigner1 ? '✅ Signer 1' : isSigner2 ? '✅ Signer 2' : '❌ Not a signer'}</p>
              <p className="text-slate-300">Signer 1 Approved: {proposalData.approvedBySigner1 ? '✅ Yes' : '❌ No'}</p>
              <p className="text-slate-300">Signer 2 Approved: {proposalData.approvedBySigner2 ? '✅ Yes' : '❌ No'}</p>
              <p className="text-slate-300">Voting Started: {proposalData.votingDeadline > 0n ? '✅ Yes' : '❌ No'}</p>
              <p className="text-slate-300">Cancelled: {proposalData.cancelled ? '✅ Yes' : '❌ No'}</p>
              <p className="text-slate-300">Executed: {proposalData.executed ? '✅ Yes' : '❌ No'}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className={`p-2 rounded-lg ${proposalData.approvedBySigner1 ? 'bg-green-900/20 border border-green-500/30' : 'bg-slate-800/50 border border-slate-700'}`}>
                <p className="text-xs text-slate-500">Signer 1</p>
                <p className={`text-xs font-bold ${proposalData.approvedBySigner1 ? 'text-green-400' : 'text-slate-500'}`}>
                  {proposalData.approvedBySigner1 ? '✅ Approved' : '⏳ Pending'}
                </p>
                {isSigner1 && (
                  <p className="text-xs text-blue-400 mt-1">(You)</p>
                )}
              </div>
              <div className={`p-2 rounded-lg ${proposalData.approvedBySigner2 ? 'bg-green-900/20 border border-green-500/30' : 'bg-slate-800/50 border border-slate-700'}`}>
                <p className="text-xs text-slate-500">Signer 2</p>
                <p className={`text-xs font-bold ${proposalData.approvedBySigner2 ? 'text-green-400' : 'text-slate-500'}`}>
                  {proposalData.approvedBySigner2 ? '✅ Approved' : '⏳ Pending'}
                </p>
                {isSigner2 && (
                  <p className="text-xs text-blue-400 mt-1">(You)</p>
                )}
              </div>
            </div>
            {/* AGC: Show current approval status */}
            <div className="mb-3 p-2 bg-slate-800/50 rounded-lg text-xs">
              <p className="text-slate-400 mb-1">Current Approval Status:</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-slate-500">Signer 1 (You)</p>
                  <p className={proposalData.approvedBySigner1 ? 'text-green-400 font-bold' : 'text-yellow-400'}>
                    {proposalData.approvedBySigner1 ? '✅ Approved' : isSigner1 ? '⏳ Click Approve' : '⏳ Waiting'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Signer 2</p>
                  <p className={proposalData.approvedBySigner2 ? 'text-green-400 font-bold' : 'text-yellow-400'}>
                    {proposalData.approvedBySigner2 ? '✅ Approved' : '⏳ Waiting'}
                  </p>
                </div>
              </div>
              {proposalData.votingDeadline > 0n && (
                <p className="text-green-400 mt-2 font-bold">✅ Both approved - Voting has started!</p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApproveProposal}
                disabled={
                  isLoading || 
                  isPending || 
                  !isConnected || 
                  (isSigner1 && proposalData.approvedBySigner1) || 
                  (isSigner2 && proposalData.approvedBySigner2) ||
                  proposalData.votingDeadline > 0n
                }
                className="flex-1 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳ Approving...' : 
                 (isSigner1 && proposalData.approvedBySigner1) || (isSigner2 && proposalData.approvedBySigner2) ? '✅ Already Approved' :
                 proposalData.votingDeadline > 0n ? '✅ Both Approved' :
                 '✅ Approve'}
              </button>
              <button
                onClick={handleCancelProposal}
                disabled={isLoading || isPending || !isConnected}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳...' : '❌ Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Step 3 - Vote (Staker Only) */}
        {currentStep === 3 && proposalData && proposalData.approvedBySigner1 && proposalData.approvedBySigner2 && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">🗳️ Step 3: Voting</h2>
            <div className="mb-3 p-2 bg-slate-800/50 rounded-lg text-xs">
              <p><span className="text-slate-500">Token:</span> {TOKEN_LABELS[proposalData.tokenType as TokenType]}</p>
              <p><span className="text-slate-500">Amount:</span> {proposalData.amount === 0n ? 'All' : formatEther(proposalData.amount)}</p>
              <p><span className="text-slate-500">To:</span> {proposalData.recipient.slice(0, 10)}...</p>
              <p className="mt-2"><span className="text-slate-500">Time Left:</span> <span className="text-yellow-400 font-bold">{getRemainingTime()}</span></p>
            </div>
            
            {/* Vote Progress Bar */}
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-green-400">YES: {yesPercentage}%</span>
                <span className="text-red-400">NO: {100 - yesPercentage}%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all" 
                  style={{ width: `${yesPercentage}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Need 51% YES to pass</p>
            </div>

            {isStaker && !hasVoted && proposalData.votingDeadline > 0n && BigInt(Date.now()) / 1000n <= proposalData.votingDeadline && (
              <div className="flex gap-2">
                <button
                  onClick={() => handleVote(true)}
                  disabled={isLoading || isPending || !isConnected}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
                >
                  👍 YES
                </button>
                <button
                  onClick={() => handleVote(false)}
                  disabled={isLoading || isPending || !isConnected}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
                >
                  👎 NO
                </button>
              </div>
            )}

            {hasVoted && (
              <p className="text-center text-green-400 text-sm">✅ You have voted</p>
            )}

            {!isStaker && (
              <p className="text-center text-slate-500 text-sm">Only stakers can vote</p>
            )}

            {/* AGC: Cancel button for signers during voting */}
            {isSigner && (
              <button
                onClick={handleCancelProposal}
                disabled={isLoading || isPending || !isConnected}
                className="w-full mt-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-xs transition-colors"
              >
                {isLoading ? '⏳ Cancelling...' : '❌ Cancel Proposal'}
              </button>
            )}
          </div>
        )}

        {/* AGC: Step 4 - Execute (Signer Only, after vote passes) */}
        {currentStep === 4 && isSigner && canExecute && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">🚀 Step 4: Execute Withdrawal</h2>
            <div className="mb-3 p-2 bg-green-900/20 border border-green-500/30 rounded-lg text-xs">
              <p className="text-green-400 font-bold">✅ Vote Passed!</p>
              <p className="text-slate-300 mt-1">Ready to execute withdrawal</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExecuteWithdrawal}
                disabled={isLoading || isPending || !isConnected}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳ Executing...' : '🚀 Execute'}
              </button>
              <button
                onClick={handleCancelProposal}
                disabled={isLoading || isPending || !isConnected}
                className="flex-1 py-3 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold text-sm transition-colors"
              >
                {isLoading ? '⏳...' : '❌ Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Step 5 - Done */}
        {currentStep === 5 && proposalData?.executed && (
          <div className="mt-4 pb-3 border-b border-white/10">
            <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg text-center">
              <p className="text-green-400 font-bold text-lg">🎉 Withdrawal Complete!</p>
              <p className="text-xs text-slate-400 mt-1">Proposal #{proposalId?.toString()} executed successfully</p>
              {executionTxHash && (
                <a
                  href={`${chainId === 84532 ? 'https://sepolia.basescan.org' : 'https://basescan.org'}/tx/${executionTxHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 text-sm underline mt-2 block"
                >
                  View on BaseScan 🔗
                </a>
              )}
            </div>
            {/* AGC: Button to start new proposal */}
            {isSigner && (
              <button
                onClick={() => {
                  // AGC: Reset state to allow new proposal
                  setProposalId(null)
                  setExecutionTxHash(null)
                  setSuccess(null)
                  setError(null)
                  setWithdrawAmount('0')
                  setVotingDurationMinutes('5')
                  setTokenType(TokenType.ETH)
                  setSelectedRecipient(FIXED_SIGNER_1)
                  setShouldAutoLoadProposal(false) // AGC: Prevent auto-loading when user wants to start new proposal
                  refetchNextProposalId()
                }}
                className="w-full mt-3 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-sm transition-colors"
              >
                📝 Start New Proposal
              </button>
            )}
          </div>
        )}

        {/* AGC: Waiting Message for Non-Signers */}
        {!isSigner && (currentStep === 1 || currentStep === 2 || currentStep === 4) && (
          <div className="mt-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-center">
            <p className="text-slate-400 text-sm">⏳ Waiting for signer action...</p>
            <p className="text-xs text-slate-500 mt-1">
              {currentStep === 1 && 'A signer needs to propose a withdrawal'}
              {currentStep === 2 && 'Both signers need to approve the proposal'}
              {currentStep === 4 && 'A signer needs to execute the withdrawal'}
            </p>
          </div>
        )}

        {/* AGC: Proposal History */}
        <div className="mt-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setIsProposalHistoryExpanded(!isProposalHistoryExpanded)}
              className="flex-1 text-left text-xs uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
            >
              <span>📊 PROPOSAL HISTORY {allProposalIds.length > 0 && `(${allProposalIds.length})`}</span>
            </button>
            <span className="text-lg text-slate-400">{isProposalHistoryExpanded ? '▼' : '▶'}</span>
          </div>
          
          {isProposalHistoryExpanded && (
            <>
              {/* AGC: Debug info for proposal history */}
              <div className="mb-2 p-2 bg-slate-800/30 rounded text-xs text-slate-400">
                <p>SM Next ID: {nextProposalIdStakingManager?.toString() || 'Loading...'} ({stakingManagerProposalIds.length} proposals)</p>
                <p>SRS Next ID: {nextProposalIdRevenueShare?.toString() || 'Loading...'} ({revenueShareProposalIds.length} proposals)</p>
                <p>Total: {allProposalIds.length} proposals</p>
                <p>Stored TX Hashes: {Object.keys(proposalTxHashes).length}</p>
              </div>
              
              <div className="space-y-2 mt-2">
                {allProposalIds.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-2">No proposals yet</p>
                ) : (
                  allProposalIds.map((proposal) => {
                    // AGC: Create unique key with contract prefix
                    const uniqueKey = `${proposal.contract}-${proposal.id.toString()}`
                    // AGC: Create unique proposal identifier for localStorage (SM-{id} or SRS-{id})
                    const proposalKey = `${proposal.contract === 'StakingManager' ? 'SM' : 'SRS'}-${proposal.id.toString()}`
                    // AGC: Use proposal ID directly for expansion tracking (BigInt)
                    const isExpanded = expandedProposals.has(proposal.id)
                    
                    return (
                      <ProposalItem
                        key={uniqueKey}
                        proposalId={proposal.id}
                        revenueShareAddress={STAKING_REVENUE_SHARE_ADDRESS}
                        stakingManagerAddress={STAKING_MANAGER_V4_ADDRESS}
                        chainId={chainId}
                        executionTxHash={proposalTxHashes[proposalKey] || proposalTxHashes[proposal.id.toString()] || (proposal.id === proposalId && proposal.contract === 'StakingManager' ? executionTxHash : null)}
                        isExpanded={isExpanded}
                        onToggleExpand={() => {
                          const newExpanded = new Set(expandedProposals)
                          if (newExpanded.has(proposal.id)) {
                            newExpanded.delete(proposal.id)
                          } else {
                            newExpanded.add(proposal.id)
                          }
                          setExpandedProposals(newExpanded)
                        }}
                      />
                    )
                  })
                )}
                
                {/* AGC: Manual TX Hash Linker (for proposals executed outside this session) */}
                {isSigner && (
                  <div className="mt-3 p-2 bg-slate-800/50 border border-slate-700 rounded-lg">
                    <p className="text-xs text-slate-400 mb-2">🔗 Link Execution TX to Proposal</p>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Proposal ID (or leave empty to auto-detect)"
                          className="flex-1 px-2 py-1 bg-slate-900/50 border border-slate-700 rounded text-xs text-white"
                          id="manual-proposal-id"
                        />
                        <input
                          type="text"
                          placeholder="TX Hash (0x...)"
                          className="flex-1 px-2 py-1 bg-slate-900/50 border border-slate-700 rounded text-xs text-white font-mono"
                          id="manual-tx-hash"
                          defaultValue="0x16035624cde8146da74d6f8e08a8b9817f459f37fc7eea0f205d2c55b160cb9e"
                        />
                        <button
                          onClick={async () => {
                            const proposalIdInput = document.getElementById('manual-proposal-id') as HTMLInputElement
                            const txHashInput = document.getElementById('manual-tx-hash') as HTMLInputElement
                            const pid = proposalIdInput?.value
                            const txHash = txHashInput?.value
                            
                            if (!txHash) {
                              setError('Please enter TX Hash')
                              return
                            }
                            
                            if (!txHash.startsWith('0x') || txHash.length !== 66) {
                              setError('Invalid TX Hash format (must be 0x followed by 64 hex characters)')
                              return
                            }
                            
                            try {
                              let pidNum: bigint
                              
                              // AGC: If proposal ID not provided, try to extract from transaction receipt
                              if (!pid && publicClient) {
                                try {
                                  setIsLoading(true)
                                  const receipt = await publicClient.getTransactionReceipt({ hash: txHash as `0x${string}` })
                                  
                                  // AGC: Look for WithdrawalExecuted event in logs
                                  // Event signature: WithdrawalExecuted(uint256 indexed proposalId, address recipient, uint8 tokenType, uint256 amount)
                                  const eventSignature = '0x' + 'WithdrawalExecuted(uint256,address,uint8,uint256)'.split('').reduce((acc, char) => {
                                    return acc + char.charCodeAt(0).toString(16).padStart(2, '0')
                                  }, '')
                                  
                                  // AGC: Try to find proposal ID from logs
                                  for (const log of receipt.logs) {
                                    if (log.address.toLowerCase() === STAKING_MANAGER_V4_ADDRESS.toLowerCase()) {
                                      // AGC: Decode the first topic (indexed proposalId)
                                      if (log.topics.length > 1) {
                                        pidNum = BigInt(log.topics[1])
                                        logger.info('Auto-detected Proposal ID from TX receipt:', pidNum.toString())
                                        break
                                      }
                                    }
                                  }
                                  
                                  if (!pidNum!) {
                                    setError('Could not auto-detect Proposal ID from transaction. Please enter it manually.')
                                    setIsLoading(false)
                                    return
                                  }
                                } catch (receiptErr) {
                                  setError('Could not fetch transaction receipt. Please enter Proposal ID manually.')
                                  setIsLoading(false)
                                  return
                                }
                              } else {
                                pidNum = BigInt(pid)
                              }
                              
                              const updatedHashes = { ...proposalTxHashes, [pidNum.toString()]: txHash }
                              setProposalTxHashes(updatedHashes)
                              localStorage.setItem('emergencyWithdrawProposalTxHashes', JSON.stringify(updatedHashes))
                              setSuccess(`Linked TX ${txHash.slice(0, 10)}... to Proposal #${pidNum.toString()}`)
                              proposalIdInput.value = ''
                              txHashInput.value = ''
                              setIsLoading(false)
                              
                              // AGC: Refresh proposal list
                              await refetchNextProposalId()
                            } catch (err: any) {
                              setIsLoading(false)
                              setError(err.message || 'Invalid Proposal ID or failed to process')
                            }
                          }}
                          disabled={isLoading}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded text-xs font-semibold transition-colors"
                        >
                          {isLoading ? '⏳...' : 'Link'}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">Enter TX Hash and click Link. If Proposal ID is empty, it will try to auto-detect from the transaction.</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  )
}

export default EmergencyWithdrawStakingManager
